import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { listServiceProviders, submitServiceRequest } from "@/lib/services.functions";
import { SERVICE_CATEGORIES } from "@/lib/service-categories";
import {
  ArrowRight,
  Check,
  Globe2,
  Search,
  ShieldCheck,
  Sparkles,
  Star,
  Wrench,
  X,
} from "lucide-react";
import { MarketingLayout as Layout, useHydratedTranslation } from "@/components/marketing/Layout";
import { buildSeo, SITE_URL } from "@/lib/seo";
import { toast } from "sonner";

export const Route = createFileRoute("/services/")({
  loader: async () => {
    try {
      const r = await listServiceProviders({ data: {} });
      return { providers: r.providers };
    } catch {
      return { providers: [] as any[] };
    }
  },
  head: () => {
    const seo = buildSeo({
      path: "/services",
      title: "Find a service pro — Reality Twin Services",
      description:
        "Painters, electricians, agencies and more: request a free quote, chat with the company's AI twin, book the appointment and pay securely online.",
    });
    const scripts = [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Reality Twin Services",
          url: `${SITE_URL}/services`,
          description: "Service provider directory with online quotes, booking and secure payment.",
        }),
      },
    ];
    return { meta: seo.meta, links: seo.links, scripts };
  },
  component: Page,
});

function Page() {
  const t = useHydratedTranslation();
  const fetchProviders = useServerFn(listServiceProviders);
  const sendRequest = useServerFn(submitServiceRequest);
  const initial = Route.useLoaderData();
  const [providers, setProviders] = useState<any[]>(initial?.providers ?? []);
  const [category, setCategory] = useState("");
  const [country, setCountry] = useState("");
  const [q, setQ] = useState("");

  // Request-quote modal state
  const [target, setTarget] = useState<any>(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    message: "",
    preferredDate: "",
    budget: "",
  });
  const [sending, setSending] = useState(false);
  const [sentFor, setSentFor] = useState<string | null>(null);

  useEffect(() => {
    fetchProviders({
      data: {
        category: category || undefined,
        country: country || undefined,
        q: q || undefined,
      },
    })
      .then((r) => setProviders(r.providers))
      .catch(() => {});
  }, [category, country, q, fetchProviders]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!target || sending) return;
    setSending(true);
    try {
      await sendRequest({
        data: {
          companyId: target.id,
          name: form.name,
          email: form.email,
          category: category || target.service_categories?.[0],
          message: form.message,
          preferredDate: form.preferredDate || undefined,
          budget: form.budget ? Number(form.budget) : undefined,
        },
      });
      setSentFor(target.name);
      setTarget(null);
      setForm({ name: "", email: "", message: "", preferredDate: "", budget: "" });
    } catch (err: any) {
      toast.error(err?.message ?? t("routes.directory.services.sendError"));
    } finally {
      setSending(false);
    }
  }

  return (
    <Layout>
      <section className="mx-auto max-w-6xl px-6 pt-24 pb-16">
        <h1 className="font-display text-5xl md:text-6xl font-semibold tracking-tight text-gradient">
          {t("routes.directory.services.title")}
        </h1>
        <p className="mt-4 text-muted-foreground max-w-2xl text-lg">
          {t("routes.directory.services.sub")}
        </p>

        <div className="mt-6 flex flex-wrap gap-2 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1.5 glass rounded-full border border-border px-3 py-1.5">
            <ShieldCheck className="h-3.5 w-3.5 text-[oklch(0.82_0.18_168)]" />{" "}
            {t("routes.directory.services.badgeSecure")}
          </span>
          <span className="inline-flex items-center gap-1.5 glass rounded-full border border-border px-3 py-1.5">
            <Sparkles className="h-3.5 w-3.5 text-[oklch(0.82_0.18_168)]" />{" "}
            {t("routes.directory.services.badgeAsk")}
          </span>
          <span className="inline-flex items-center gap-1.5 glass rounded-full border border-border px-3 py-1.5">
            <Check className="h-3.5 w-3.5 text-[oklch(0.82_0.18_168)]" />{" "}
            {t("routes.directory.services.badgeFree")}
          </span>
        </div>

        <div className="mt-8 flex flex-wrap gap-2 items-center">
          <button
            onClick={() => setCategory("")}
            className={`text-xs px-3.5 py-1.5 rounded-full border ${category === "" ? "brand-gradient text-black border-transparent" : "glass border-border text-muted-foreground hover:bg-white/5"}`}
          >
            {t("routes.directory.services.all")}
          </button>
          {SERVICE_CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              className={`text-xs px-3.5 py-1.5 rounded-full border ${category === c ? "brand-gradient text-black border-transparent" : "glass border-border text-muted-foreground hover:bg-white/5"}`}
            >
              {t(`routes.directory.categories.${c}`)}
            </button>
          ))}
          <div className="ml-auto flex flex-wrap gap-2">
            <input
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              placeholder={t("routes.directory.services.countryPh")}
              className="glass w-28 rounded-full border border-border px-3 py-1.5 text-xs focus:outline-none"
            />
            <div className="glass rounded-full border border-border flex items-center gap-2 px-3 py-1.5">
              <Search className="h-3.5 w-3.5 text-muted-foreground" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t("routes.directory.services.searchPh")}
                className="bg-transparent text-xs focus:outline-none w-36"
              />
            </div>
          </div>
        </div>

        {sentFor && (
          <div className="mt-6 glass-strong rounded-2xl p-4 flex items-center gap-3 border border-[oklch(0.82_0.18_168_/_0.35)]">
            <Check className="h-5 w-5 text-[oklch(0.82_0.18_168)]" />
            <span className="text-sm">
              {t("routes.directory.services.sentPre")}
              <strong>{sentFor}</strong>
              {t("routes.directory.services.sentPost")}
            </span>
          </div>
        )}

        <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {providers.length === 0 && (
            <div className="col-span-full glass-strong rounded-2xl p-10 text-center text-sm text-muted-foreground">
              {t("routes.directory.services.empty")}
              <Link to="/auth" className="text-[oklch(0.82_0.18_168)] hover:underline">
                {t("routes.directory.services.emptyCta")}
              </Link>
            </div>
          )}
          {providers.map((p: any) => (
            <div key={p.id} className="glass-strong rounded-2xl p-5 flex flex-col">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-xl brand-gradient overflow-hidden flex items-center justify-center text-black font-bold shrink-0">
                  {p.logo_url ? (
                    <img src={p.logo_url} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <Wrench className="h-5 w-5" />
                  )}
                </div>
                <div className="min-w-0">
                  <div className="font-display text-lg truncate">{p.name}</div>
                  <div className="text-xs text-muted-foreground flex items-center gap-2">
                    {p.rating_count > 0 && (
                      <span className="inline-flex items-center gap-1 text-[oklch(0.85_0.15_85)]">
                        <Star className="h-3 w-3 fill-current" /> {p.rating_avg}
                        <span className="text-muted-foreground">({p.rating_count})</span>
                      </span>
                    )}
                    {p.country && (
                      <span className="inline-flex items-center gap-1">
                        <Globe2 className="h-3 w-3" /> {p.country}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              {(p.service_categories ?? []).length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {(p.service_categories ?? []).slice(0, 4).map((c: string) => (
                    <span
                      key={c}
                      className="text-[11px] px-2 py-0.5 rounded-md bg-white/5 border border-border text-muted-foreground"
                    >
                      {t(`routes.directory.categories.${c}`, { defaultValue: c })}
                    </span>
                  ))}
                </div>
              )}
              {p.description && (
                <p className="mt-3 text-sm text-muted-foreground line-clamp-3 flex-1">
                  {p.description}
                </p>
              )}
              <div className="mt-4 flex gap-2">
                <button
                  onClick={() => setTarget(p)}
                  className="flex-1 rounded-full brand-gradient text-black px-4 py-2 text-sm font-medium"
                >
                  {t("routes.directory.services.requestQuote")}
                </button>
                <Link
                  to="/marketplace/$id"
                  params={{ id: p.id }}
                  className="rounded-full glass border border-border px-4 py-2 text-sm hover:bg-white/5"
                >
                  {t("routes.directory.services.askTwin")}
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      {target && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setTarget(null)}
          />
          <form
            onSubmit={submit}
            className="relative glass-strong rounded-2xl p-6 w-full max-w-lg border border-border"
          >
            <button
              type="button"
              onClick={() => setTarget(null)}
              className="absolute top-4 right-4 text-muted-foreground hover:text-white"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="font-display text-xl font-semibold">
              {t("routes.directory.services.modalTitle", { name: target.name })}
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              {t("routes.directory.services.modalSub")}
            </p>
            <div className="mt-5 grid sm:grid-cols-2 gap-3">
              <input
                required
                maxLength={120}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={t("routes.directory.services.phName")}
                className="rounded-xl bg-white/5 border border-border px-3 py-2 text-sm focus:outline-none focus:border-[oklch(0.82_0.18_168_/_0.6)]"
              />
              <input
                required
                type="email"
                maxLength={200}
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder={t("routes.directory.services.phEmail")}
                className="rounded-xl bg-white/5 border border-border px-3 py-2 text-sm focus:outline-none focus:border-[oklch(0.82_0.18_168_/_0.6)]"
              />
              <input
                value={form.preferredDate}
                onChange={(e) => setForm({ ...form, preferredDate: e.target.value })}
                placeholder={t("routes.directory.services.phDate")}
                maxLength={120}
                className="rounded-xl bg-white/5 border border-border px-3 py-2 text-sm focus:outline-none focus:border-[oklch(0.82_0.18_168_/_0.6)]"
              />
              <input
                value={form.budget}
                onChange={(e) => setForm({ ...form, budget: e.target.value })}
                placeholder={t("routes.directory.services.phBudget")}
                type="number"
                min={0}
                className="rounded-xl bg-white/5 border border-border px-3 py-2 text-sm focus:outline-none focus:border-[oklch(0.82_0.18_168_/_0.6)]"
              />
              <textarea
                required
                minLength={5}
                maxLength={2000}
                rows={4}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder={t("routes.directory.services.phMessage")}
                className="sm:col-span-2 rounded-xl bg-white/5 border border-border px-3 py-2 text-sm resize-none focus:outline-none focus:border-[oklch(0.82_0.18_168_/_0.6)]"
              />
            </div>
            <button
              type="submit"
              disabled={sending}
              className="mt-4 w-full rounded-full brand-gradient text-black px-5 py-2.5 text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-1.5"
            >
              {sending
                ? t("routes.directory.services.sending")
                : t("routes.directory.services.send")}{" "}
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </form>
        </div>
      )}
    </Layout>
  );
}
